import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { leaveService } from "../../../api/LeaveService/leaveService";
import { toast } from 'react-toastify';

const SubmitLeaveModal = ({ onClose }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    employeeId: id || '',
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    totalDays: 1,
    reason: '',
  });

  // Automatically calculate total days when startDate or endDate changes
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start) {
        // Calculate difference in milliseconds, convert to days, and include both start and end days (+1)
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        setFormData((prev) => ({ ...prev, totalDays: diffDays }));
      }
    }
  }, [formData.startDate, formData.endDate]);

  // Fetch leave types using react-query
  const { data: leaveTypesResponse, isLoading: isLoadingLeaveTypes } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: () => leaveService.getLeaveTypes(),
  });

  const leaveTypesList = Array.isArray(leaveTypesResponse) 
    ? leaveTypesResponse 
    : leaveTypesResponse?.data || [];

  // Mutation using leaveService.createLeaveRequest
  const mutation = useMutation({
    mutationFn: (data) => leaveService.createLeaveRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveList', id] });
      queryClient.invalidateQueries({ queryKey: ['employeeLeaves', id] });
      toast.success("Leave request submitted successfully!");
      onClose();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to submit leave request");
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ...formData,
      employeeId: id || formData.employeeId
    });
  };

  return ReactDOM.createPortal(
    <div 
      className="modal show d-flex align-items-center justify-content-center" 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100vw', 
        height: '100vh', 
        backgroundColor: 'rgba(0, 0, 0, 0.5)', 
        zIndex: 99999, 
        margin: 0,
        padding: '1rem',
        overflowY: 'auto'
      }}
    >
      <div 
        className="w-100 m-auto" 
        style={{ maxWidth: '500px', pointerEvents: 'auto' }}
      >
        <form className="modal-content shadow-lg border-0 w-100" onSubmit={handleSubmit}>
          <div className="modal-header bg-light">
            <h5 className="modal-title fw-bold fs-6 text-dark">Submit Leave Request</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          
          <div className="modal-body p-4">
            <div className="mb-3">
              <label className="form-label small fw-bold">Leave Type</label>
              <select 
                className="form-select form-select-sm" 
                value={formData.leaveTypeId} 
                onChange={(e) => setFormData({...formData, leaveTypeId: e.target.value})}
                required
              >
                <option value="">
                  {isLoadingLeaveTypes ? "Loading leave types..." : "Select Leave Type"}
                </option>
                {leaveTypesList.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label small fw-bold">Start Date</label>
                <input 
                  type="date"
                  className="form-control form-control-sm" 
                  value={formData.startDate} 
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-bold">End Date</label>
                <input 
                  type="date"
                  className="form-control form-control-sm" 
                  value={formData.endDate} 
                  min={formData.startDate} // Prevent selecting end date prior to start date
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label small fw-bold">Total Days</label>
              <input 
                type="number"
                step="0.5"
                className="form-control form-control-sm" 
                value={formData.totalDays} 
                onChange={(e) => setFormData({...formData, totalDays: parseFloat(e.target.value) || 0})}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label small fw-bold">Reason</label>
              <textarea 
                className="form-control form-control-sm" 
                value={formData.reason} 
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                placeholder="Enter reason for leave..."
                rows="3"
                required
              />
            </div>
          </div>

          <div className="modal-footer bg-light">
            <button 
              type="button" 
              className="btn btn-secondary btn-sm px-3" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary btn-sm px-3" 
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default SubmitLeaveModal;